import { useEffect, useState } from 'react';
import type { ConversationListEntry } from '../types';
import { useChat } from '../store';
import { api } from '../api';
import { Avatar } from './Avatar';
import {
  IconArchive,
  IconBell,
  IconBellOff,
  IconCheck,
  IconChevronRight,
  IconFile,
  IconHeart,
  IconImage,
  IconMic,
  IconStar,
  IconVideo,
} from './Icon';
import { toast } from './ToastHost';
import { useDraftIndex } from '../utils/drafts';
import { relativeTime } from '../utils/time';

const IMAGE_RE = /^\[.+\.(jpg|jpeg|png|gif|webp|heic|heif|avif|bmp|svg)\]$/i;
const VIDEO_RE = /^\[.+\.(mp4|mov|webm|mkv|m4v|avi)\]$/i;
const AUDIO_RE = /^\[.+\.(mp3|wav|ogg|m4a|flac|aac)\]$/i;
const DOC_RE = /^\[.+\.(pdf|docx?|xlsx?|pptx?|txt|csv|md|key|pages|numbers)\]$/i;
const ATTACHMENT_RE = /^\[.+\]$/;

interface ConversationListItemProps {
  conversation: ConversationListEntry;
  selected: boolean;
  onClick: () => void;
}

export function ConversationListItem({
  conversation,
  selected,
  onClick,
}: ConversationListItemProps) {
  const me = useChat((s) => s.me);
  const onlineUserIds = useChat((s) => s.onlineUserIds);
  const removeConversation = useChat((s) => s.removeConversation);
  // Channels the user can move conversations into. Filtered to ones the
  // user is a member of (server-side returns only those anyway).
  const channels = useChat((s) => s.channels);
  const setConversationChannel = useChat((s) => s.setConversationChannel);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [moveSubmenuOpen, setMoveSubmenuOpen] = useState(false);
  // Drafts take priority over the last-message preview — WhatsApp pattern.
  // The hook re-renders the whole sidebar when any draft changes; cheap
  // because the index is just a {convId: text} map and we only read one
  // entry per row.
  const draftIndex = useDraftIndex();
  const draft = draftIndex[conversation.id]?.trim() ?? '';

  useEffect(() => {
    if (!menuPos) return;
    const close = () => setMenuPos(null);
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
      window.removeEventListener('blur', close);
    };
  }, [menuPos]);

  // For DMs: identify "the other" participant.
  // For groups: use the group name (or fallback to the participants).
  const others = conversation.participants.filter((p) => p.id !== me?.id);

  let title: string;
  let subtitle: string | null;
  let avatarKey: { id: string; displayName: string | null; email: string };
  let online: boolean | undefined;

  if (conversation.type === 'dm') {
    const other = others[0];
    if (!other) {
      title = 'Empty conversation';
      subtitle = null;
      avatarKey = { id: conversation.id, displayName: null, email: '?' };
    } else {
      title = other.displayName ?? other.email;
      subtitle = other.displayName ? other.email : null;
      avatarKey = { id: other.id, displayName: other.displayName, email: other.email };
      online = onlineUserIds[other.id] ?? false;
    }
  } else {
    title = conversation.name ?? others.map((p) => p.displayName ?? p.email).join(', ');
    subtitle = `${conversation.participants.length} members`;
    avatarKey = { id: conversation.id, displayName: title, email: title };
  }

  const lastMessagePreview = (() => {
    if (!conversation.lastMessage) {
      return { kind: 'text' as const, content: 'No messages yet', prefix: '' };
    }
    if (conversation.lastMessage.deleted) {
      return { kind: 'text' as const, content: 'Message deleted', prefix: '' };
    }
    const isMe = conversation.lastMessage.senderId === me?.id;
    const prefix =
      conversation.type !== 'dm' && !isMe
        ? (() => {
            const sender = conversation.participants.find(
              (p) => p.id === conversation.lastMessage!.senderId,
            );
            return sender ? `${sender.displayName ?? sender.email}: ` : '';
          })()
        : isMe
          ? 'You: '
          : '';

    const body = conversation.lastMessage.body ?? '';
    const kind = conversation.lastMessage.kind;

    if (kind === 'voice') {
      return { kind: 'voice' as const, content: 'Voice message', prefix };
    }
    if (IMAGE_RE.test(body)) {
      return { kind: 'image' as const, content: 'Photo', prefix };
    }
    if (VIDEO_RE.test(body)) {
      return { kind: 'video' as const, content: 'Video', prefix };
    }
    if (AUDIO_RE.test(body)) {
      return { kind: 'audio' as const, content: 'Audio message', prefix };
    }
    if (DOC_RE.test(body)) {
      // Pull just the filename out of the [brackets].
      const filename = body.replace(/^\[|\]$/g, '');
      return { kind: 'doc' as const, content: filename, prefix };
    }
    if (ATTACHMENT_RE.test(body)) {
      const filename = body.replace(/^\[|\]$/g, '');
      return { kind: 'doc' as const, content: filename, prefix };
    }
    return { kind: 'text' as const, content: body, prefix };
  })();

  const lastMessageTime = conversation.lastMessage
    ? relativeTime(conversation.lastMessage.createdAt)
    : '';

  const hasUnread = conversation.unreadCount > 0;

  async function toggleFavorite() {
    setMenuPos(null);
    try {
      await api.setFavorite(conversation.id, !conversation.isFavorite);
      const state = useChat.getState();
      const idx = state.conversations.findIndex((c) => c.id === conversation.id);
      if (idx !== -1) {
        const updated = [...state.conversations];
        updated[idx] = { ...updated[idx]!, isFavorite: !conversation.isFavorite };
        useChat.setState({ conversations: updated });
      }
    } catch {
      /* ignore */
    }
  }

  async function markRead() {
    setMenuPos(null);
    if (!conversation.lastMessage) return;
    try {
      await api.markRead(conversation.id, conversation.lastMessage.id);
      const state = useChat.getState();
      const idx = state.conversations.findIndex((c) => c.id === conversation.id);
      if (idx !== -1) {
        const updated = [...state.conversations];
        updated[idx] = { ...updated[idx]!, unreadCount: 0 };
        useChat.setState({ conversations: updated });
      }
    } catch {
      /* ignore */
    }
  }

  async function mute() {
    setMenuPos(null);
    const next = conversation.mutedUntil ? null : '2999-12-31T23:59:59.999Z';
    try {
      await api.updateConversationMember(conversation.id, { mutedUntil: next });
      const state = useChat.getState();
      const idx = state.conversations.findIndex((c) => c.id === conversation.id);
      if (idx !== -1) {
        const updated = [...state.conversations];
        updated[idx] = { ...updated[idx]!, mutedUntil: next };
        useChat.setState({ conversations: updated });
      }
    } catch {
      /* ignore */
    }
  }

  async function moveToChannel(channelId: string | null) {
    setMenuPos(null);
    setMoveSubmenuOpen(false);
    // Optimistic local update — real propagation arrives via the
    // conversation:moved socket event but the user sees instant
    // feedback in the sidebar grouping.
    setConversationChannel(conversation.id, channelId);
    try {
      await api.moveConversationToChannel(conversation.id, channelId);
      const channelName = channelId
        ? useChat.getState().channels.find((c) => c.id === channelId)?.name ?? 'channel'
        : 'no channel';
      toast(`Moved to ${channelName}.`, 'success');
    } catch {
      // Roll back the optimistic move on failure.
      setConversationChannel(conversation.id, conversation.channelId);
      toast("Couldn't move that chat.", 'error');
    }
  }

  async function archive() {
    setMenuPos(null);
    try {
      await api.setArchived(conversation.id, true);
      removeConversation(conversation.id);
    } catch {
      /* ignore */
    }
  }

  return (
    <div
      className="group relative"
      onContextMenu={(e) => {
        e.preventDefault();
        // Clamp to the viewport so the menu never opens clipped off the edge
        // of the embedded dock panel (only ~640px tall).
        const MW = 216, MH = 300;
        const x = Math.max(8, Math.min(e.clientX, window.innerWidth - MW - 8));
        const y = Math.max(8, Math.min(e.clientY, window.innerHeight - MH - 8));
        setMenuPos({ x, y });
      }}
    >
    <button
      onClick={onClick}
      className={`group relative w-full text-left px-3 py-2.5 flex items-center gap-3 rounded-xl transition-all duration-300 ease-spring ${
        selected
          ? 'bg-zinc-900 surface-raised'
          : 'hover:bg-zinc-900/60'
      }`}
    >
      {/* Selection accent — vertical line on the left edge. */}
      {selected && (
        <span
          aria-hidden
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-7 rounded-full bg-gradient-to-b from-indigo-400 to-indigo-500"
        />
      )}
      <Avatar
        id={avatarKey.id}
        displayName={avatarKey.displayName}
        email={avatarKey.email}
        online={online}
        size={40}
        kind={conversation.type === 'dm' ? 'user' : 'conversation'}
        avatarUpdatedAt={
          conversation.type === 'dm' ? undefined : conversation.avatarUpdatedAt
        }
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <h3
              className={`text-sm truncate ${
                hasUnread ? 'font-semibold text-zinc-50' : 'font-medium text-zinc-100'
              }`}
            >
              {title}
            </h3>
            {conversation.isFavorite && (
              <span className="shrink-0 text-amber-400">
                <IconStar size={11} filled />
              </span>
            )}
            {conversation.bridge?.network === 'whatsapp' && (
              <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-emerald-600/20 text-emerald-300">
                WhatsApp
              </span>
            )}
          </div>
          {lastMessageTime && (
            <span
              className={`text-[10px] shrink-0 tabular-nums ${
                hasUnread ? 'text-emerald-400 font-medium' : 'text-zinc-500'
              }`}
            >
              {lastMessageTime}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          {draft ? (
            // A typed-but-unsent draft beats the last-message preview. The
            // "Draft:" label is rendered in rose so the visual cue is
            // unmistakable; matches WhatsApp's "[Draft]" prefix in red.
            <div className="flex items-center gap-1 min-w-0 text-xs text-zinc-300">
              <span className="shrink-0 text-rose-400 font-medium">Draft:</span>
              <span className="truncate">{draft}</span>
            </div>
          ) : (
            <div
              className={`flex items-center gap-1 min-w-0 text-xs ${
                hasUnread ? 'text-zinc-300' : 'text-zinc-500'
              }`}
            >
              {lastMessagePreview.prefix && (
                <span className="shrink-0">{lastMessagePreview.prefix}</span>
              )}
              <PreviewIcon kind={lastMessagePreview.kind} muted={!hasUnread} />
              <span
                className={`truncate ${
                  lastMessagePreview.kind !== 'text' ? 'italic' : ''
                }`}
              >
                {lastMessagePreview.content || subtitle || ''}
              </span>
            </div>
          )}
          {hasUnread && (
            <span className="shrink-0 bg-emerald-500 text-zinc-950 text-[10px] font-semibold tabular-nums rounded-full px-1.5 py-0.5 min-w-[18px] text-center shadow-[0_0_0_2px_rgb(var(--zinc-950))]">
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
      <button
        type="button"
        aria-label="Chat options"
        onClick={(e) => {
          e.stopPropagation();
          if (menuPos) {
            setMenuPos(null);
            return;
          }
          const r = e.currentTarget.getBoundingClientRect();
          const MW = 216,
            MH = 300;
          const x = Math.max(8, Math.min(r.right - MW, window.innerWidth - MW - 8));
          const y = Math.max(8, Math.min(r.bottom + 4, window.innerHeight - MH - 8));
          setMenuPos({ x, y });
        }}
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-100 bg-zinc-900 hover:bg-zinc-800 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="19" cy="12" r="1.6" />
        </svg>
      </button>
    {menuPos && (
      <div
        role="menu"
        onClick={(e) => e.stopPropagation()}
        className="fixed z-50 min-w-[12rem] glass-chrome hairline rounded-xl shadow-elevation overflow-hidden animate-scale-in"
        style={{ left: menuPos.x, top: menuPos.y }}
      >
        <MenuItem
          icon={
            <IconHeart
              size={13}
              className={
                conversation.isFavorite ? 'text-amber-400' : 'text-zinc-400'
              }
              filled={conversation.isFavorite}
            />
          }
          label={conversation.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          onClick={toggleFavorite}
        />
        <MenuItem
          icon={<IconCheck size={13} className="text-zinc-400" />}
          label="Mark as read"
          disabled={conversation.unreadCount === 0}
          onClick={markRead}
        />
        <MenuItem
          icon={
            conversation.mutedUntil ? (
              <IconBell size={13} className="text-zinc-400" />
            ) : (
              <IconBellOff size={13} className="text-zinc-400" />
            )
          }
          label={conversation.mutedUntil ? 'Unmute' : 'Mute'}
          onClick={mute}
        />
        {/* Move to channel — top-level menu item that expands inline
            below itself when clicked. Inline expansion (vs. a side-
            popping submenu) keeps the choices visible against the
            menu's existing column without risk of clipping at the
            screen edge. The chevron rotates to signal expansion. */}
        {channels.length > 0 && (
          <>
            <button
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                setMoveSubmenuOpen((v) => !v);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs text-zinc-200 hover:bg-zinc-800 transition"
            >
              <span className="shrink-0">
                <IconChevronRight
                  size={13}
                  className={`text-zinc-400 transition-transform ${
                    moveSubmenuOpen ? 'rotate-90' : ''
                  }`}
                />
              </span>
              <span className="flex-1">Move to…</span>
              <span className="text-[10px] text-zinc-500 max-w-[6rem] truncate">
                {conversation.channelId
                  ? channels.find((c) => c.id === conversation.channelId)?.name ?? '—'
                  : 'No channel'}
              </span>
            </button>
            {moveSubmenuOpen && (
              <div className="bg-zinc-950/40 border-t border-zinc-900 max-h-56 overflow-y-auto">
                {/* "No channel" sentinel — strips the conversation out
                    of any current grouping. Useful for users who want
                    a chat to live only in the All filter. */}
                <button
                  role="menuitem"
                  onClick={() => void moveToChannel(null)}
                  className={`w-full flex items-center gap-2 pl-9 pr-3 py-1.5 text-left text-[11px] hover:bg-zinc-800 transition ${
                    conversation.channelId === null ? 'text-indigo-300' : 'text-zinc-300'
                  }`}
                >
                  <span className="text-zinc-500">∅</span>
                  <span className="flex-1 italic">No channel</span>
                  {conversation.channelId === null && <IconCheck size={11} />}
                </button>
                {channels.map((c) => {
                  const isCurrent = conversation.channelId === c.id;
                  return (
                    <button
                      key={c.id}
                      role="menuitem"
                      onClick={() => void moveToChannel(c.id)}
                      className={`w-full flex items-center gap-2 pl-9 pr-3 py-1.5 text-left text-[11px] hover:bg-zinc-800 transition ${
                        isCurrent ? 'text-indigo-300' : 'text-zinc-200'
                      }`}
                    >
                      <span className="text-sm shrink-0 w-4 text-center">
                        {c.iconEmoji ?? '#'}
                      </span>
                      <span className="flex-1 truncate">{c.name}</span>
                      {isCurrent && <IconCheck size={11} />}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
        <MenuItem
          icon={<IconArchive size={13} className="text-zinc-400" />}
          label="Archive"
          onClick={archive}
          destructive
        />
      </div>
    )}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  disabled,
  destructive,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
        destructive
          ? 'text-zinc-200 hover:bg-rose-500/10 hover:text-rose-300'
          : 'text-zinc-200 hover:bg-zinc-800'
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1">{label}</span>
    </button>
  );
}

function PreviewIcon({
  kind,
  muted,
}: {
  kind: 'text' | 'voice' | 'image' | 'video' | 'audio' | 'doc';
  muted: boolean;
}) {
  if (kind === 'text') return null;
  const cls = `shrink-0 ${muted ? 'text-zinc-500' : 'text-zinc-400'}`;
  switch (kind) {
    case 'image':
      return <IconImage size={11} strokeWidth={1.75} className={cls} />;
    case 'video':
      return <IconVideo size={11} strokeWidth={1.75} className={cls} />;
    case 'voice':
    case 'audio':
      return <IconMic size={11} strokeWidth={1.75} className={cls} />;
    case 'doc':
      return <IconFile size={11} strokeWidth={1.75} className={cls} />;
  }
}
